/**
 * SigningPage
 *
 * Top-level (no auth, no Layout) page that recipients land on from the
 * signing email. URL: /a/arche_document_sign/sign/:token. The route is
 * registered before /a/:id/* in App.tsx so it doesn't dispatch through the
 * arche-interaction wrapper.
 *
 * Flow:
 *   token → consent → sign (review + fill fields) → submit → confirmation.
 *
 * Compliance shape: every action that produces an audit-trail event (view,
 * consent, sign, decline) sends IP + UA on the server side via the request
 * itself — we never trust client-supplied identity.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, DocumentSignFieldResponse, DocumentSignSigningFieldSubmission } from '@/lib/api';
import { addBreadcrumb, traceEnd, traceError, traceStart } from '@/lib/telemetry';
import { useAuth } from '@/auth';
import { HolderSwitcher, useHolderSwitcher } from '@/components/HolderSwitcher';
import {
  useSigningSession,
  useAcceptConsent,
  useSubmitFields,
  useSubmitSignature,
  useDeclineSignature,
  useSetDeliveryHolder,
} from '../hooks/useSigningSession';
import { ConsentDisclosure } from '../components/ConsentDisclosure';
import { SignaturePad } from '../components/SignaturePad';
import { PdfPageView, usePdfDocument } from '../components/PdfPageView';
import { useRecipientSealedDownload } from '../hooks/useSealedDownload';

type Step = 'loading' | 'consent' | 'sign' | 'declining' | 'submitted' | 'declined' | 'error';

interface FieldFill {
  /** Either a string value (text/checkbox/date/name) or null */
  value?: string | null;
  /** Base64 PNG data URL — only set on signature/initial fields */
  dataUrl?: string | null;
}

const PAGE_RENDER_WIDTH = 720;

export function SigningPage() {
  const { token } = useParams<{ token: string }>();
  const session = useSigningSession(token);
  const acceptConsent = useAcceptConsent(token ?? '');
  const submitFields = useSubmitFields(token ?? '');
  const submitSignature = useSubmitSignature(token ?? '');
  const declineSignature = useDeclineSignature(token ?? '');

  const [step, setStep] = useState<Step>('consent');
  const [fills, setFills] = useState<Record<string, FieldFill>>({});
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const signSectionRef = useRef<HTMLElement | null>(null);

  // Announce step changes to assistive tech via aria-live below — also
  // emit a breadcrumb so a CloudWatch trace shows the recipient's path.
  useEffect(() => {
    addBreadcrumb('document-sign', `Signing step: ${step}`);
  }, [step]);

  const isLoading = session.isLoading;
  const data = session.data;

  // Seed `fills` with sensible defaults for auto-fillable fields the first
  // time the session lands. Without this, `signer_name` and `date_signed`
  // render empty (the renderers fall back to the recipient name / today's
  // date for DISPLAY but never populate `fills`), and the
  // `requiredFieldsRemaining` counter — which inspects `fills[fieldId].value`
  // — never decrements past those fields. End result: Finish Signing stays
  // disabled forever. The submit handler downstream already had matching
  // fallbacks for the eventual payload; this just hoists that knowledge
  // into the UI state so the counter and the renderers see it too.
  const fillSeededRef = useRef(false);
  useEffect(() => {
    if (!data || fillSeededRef.current) return;
    const seeded: Record<string, FieldFill> = {};
    const todayISO = new Date().toISOString().split('T')[0];
    for (const f of data.fields) {
      if (f.type === 'signer_name' && data.recipient.name) {
        seeded[f.fieldId] = { value: data.recipient.name };
      } else if (f.type === 'date_signed') {
        seeded[f.fieldId] = { value: todayISO };
      }
    }
    if (Object.keys(seeded).length > 0) {
      setFills((prev) => ({ ...seeded, ...prev }));
    }
    fillSeededRef.current = true;
  }, [data]);

  // Group fields by file so we can render each PDF + its overlay together.
  const fieldsByFile = useMemo(() => {
    const m: Record<string, DocumentSignFieldResponse[]> = {};
    if (!data) return m;
    for (const f of data.fields) {
      if (!m[f.fileId]) m[f.fileId] = [];
      m[f.fileId].push(f);
    }
    return m;
  }, [data]);

  const requiredFieldsRemaining = useMemo(() => {
    if (!data) return 0;
    return data.fields.filter((f) => {
      if (!f.required) return false;
      const fill = fills[f.fieldId];
      const isSignatureType = f.type === 'signature' || f.type === 'initial';
      if (isSignatureType) return !fill?.dataUrl;
      if (f.type === 'checkbox') {
        return !fill?.value;
      }
      return !fill?.value || fill.value.length === 0;
    }).length;
  }, [data, fills]);

  const handleAcceptConsent = useCallback(async () => {
    setSubmitError(null);
    const traceId = traceStart('document-sign:signing-consent');
    try {
      await acceptConsent.mutateAsync();
      traceEnd(traceId);
      addBreadcrumb('document-sign', 'Recipient accepted consent');
      setStep('sign');
      // Move keyboard focus into the sign area so screen-reader users hear
      // the new state and don't have to hunt for the next interactive element.
      requestAnimationFrame(() => signSectionRef.current?.focus());
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : String(err));
      setSubmitError(err instanceof Error ? err.message : 'Failed to record consent');
    }
  }, [acceptConsent]);

  const handleSubmit = useCallback(async () => {
    if (!data) return;
    setSubmitError(null);
    const traceId = traceStart('document-sign:signing-submit', {
      envelopeId: data.envelope.envelopeId,
      fieldCount: data.fields.length,
    });
    const payload: DocumentSignSigningFieldSubmission[] = data.fields.map((f) => {
      const fill = fills[f.fieldId];
      const isSignatureType = f.type === 'signature' || f.type === 'initial';
      if (isSignatureType) {
        return { fieldId: f.fieldId, signatureDataUrl: fill?.dataUrl ?? null };
      }
      if (f.type === 'date_signed' && !fill?.value) {
        return { fieldId: f.fieldId, value: new Date().toISOString().split('T')[0] };
      }
      if (f.type === 'signer_name' && !fill?.value) {
        return { fieldId: f.fieldId, value: data.recipient.name ?? '' };
      }
      return { fieldId: f.fieldId, value: fill?.value ?? null };
    });

    try {
      await submitFields.mutateAsync(payload);
      const result = await submitSignature.mutateAsync();
      traceEnd(traceId, { envelopeCompleted: result.envelopeCompleted });
      addBreadcrumb('document-sign', 'Recipient submitted signature', {
        envelopeCompleted: result.envelopeCompleted,
      });
      setStep('submitted');
      setSubmitError(null);
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : String(err));
      // Field state is preserved in `fills` — recipient can retry without
      // losing what they typed / drew. Mid-submit network failure is the
      // failure mode this most matters for.
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit signature');
    }
  }, [data, fills, submitFields, submitSignature]);

  const handleDeclineConfirm = useCallback(async () => {
    setSubmitError(null);
    const traceId = traceStart('document-sign:signing-decline');
    try {
      await declineSignature.mutateAsync(declineReason || 'Recipient declined');
      traceEnd(traceId);
      addBreadcrumb('document-sign', 'Recipient declined');
      setStep('declined');
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : String(err));
      setSubmitError(err instanceof Error ? err.message : 'Failed to decline');
    }
  }, [declineSignature, declineReason]);

  if (!token) return <CenteredMessage title="Invalid link" />;

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-3">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (session.error) {
    const apiErr = session.error instanceof ApiError ? session.error : null;
    if (apiErr?.status === 410) {
      // A recipient returning to their signing link after signing gets the
      // same post-sign view as the moment they signed — their completed copy
      // is downloadable there once every party has signed.
      if (apiErr.code === 'ALREADY_SIGNED') {
        return <SubmittedConfirmation token={token ?? ''} />;
      }
      // A completed envelope — the recipient followed the link in their
      // "document complete" email. Show the same download view a just-signed
      // recipient gets, so the link actually delivers the sealed PDF.
      if (apiErr.code === 'ENVELOPE_TERMINAL' && apiErr.envelopeStatus === 'completed') {
        return <SubmittedConfirmation token={token ?? ''} />;
      }
      // The middleware separates the remaining terminal-state codes — surface
      // specific copy so the recipient knows what happened. Default 410
      // message for anything we haven't enumerated.
      const { title, description } = (() => {
        switch (apiErr.code) {
          case 'TOKEN_EXPIRED':
            return {
              title: 'This signing link has expired',
              description: 'Signing links are good for 30 days. Ask the sender for a fresh link.',
            };
          case 'ALREADY_DECLINED':
            return {
              title: 'You already declined this envelope',
              description: 'The sender has been notified.',
            };
          case 'ENVELOPE_TERMINAL':
            return {
              title: 'This envelope is no longer active',
              description: apiErr.message ?? 'The envelope was voided, declined, or expired.',
            };
          default:
            return {
              title: 'This signing link is no longer active',
              description: apiErr.message ?? 'The envelope has expired or already finished.',
            };
        }
      })();
      return <CenteredMessage title={title} description={description} />;
    }
    if (apiErr?.status === 403) {
      return (
        <CenteredMessage
          title="Not your turn yet"
          description="An earlier signer needs to finish first. We'll email you when it's your turn."
        />
      );
    }
    return (
      <CenteredMessage
        title="Signing link not found"
        description={apiErr?.message ?? 'This link is invalid or has been revoked.'}
      />
    );
  }

  if (!data) return null;

  if (step === 'submitted') {
    return <SubmittedConfirmation token={token} />;
  }
  if (step === 'declined') {
    return (
      <CenteredMessage
        title="You've declined to sign"
        description="The sender has been notified. You can close this page."
      />
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold">Document Sign</h1>
            <p className="text-xs text-muted-foreground">{data.envelope.title}</p>
          </div>
          <span className="text-xs text-muted-foreground">
            Signing as {data.recipient.name ?? data.recipient.email ?? 'recipient'}
          </span>
        </div>
      </div>

      {step === 'consent' && (
        <main className="max-w-5xl mx-auto px-4 py-8">
          <ConsentDisclosure
            envelopeTitle={data.envelope.title}
            ownerName={data.envelope.ownerName}
            disclosureText={data.consentDisclosure}
            onAccept={handleAcceptConsent}
            onDecline={() => setStep('declining')}
            pending={acceptConsent.isPending}
          />
          {submitError && <p className="text-sm text-destructive mt-4">{submitError}</p>}
        </main>
      )}

      {step === 'sign' && (
        <SignSection
          data={data}
          fields={data.fields}
          fieldsByFile={fieldsByFile}
          fills={fills}
          activeFieldId={activeFieldId}
          setActiveFieldId={setActiveFieldId}
          setFills={setFills}
          requiredFieldsRemaining={requiredFieldsRemaining}
          submitting={submitFields.isPending || submitSignature.isPending}
          submitError={submitError}
          onSubmit={handleSubmit}
          onDecline={() => setStep('declining')}
          message={data.envelope.message}
          sectionRef={signSectionRef}
        />
      )}

      {step === 'declining' && (
        <main className="max-w-2xl mx-auto px-4 py-8 space-y-4">
          <h2 className="text-xl font-semibold">Decline to sign</h2>
          <p className="text-sm text-muted-foreground">
            Tell the sender briefly why you're declining. The envelope will be marked declined and
            no further signatures will be requested.
          </p>
          <Textarea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Reason (optional)"
            maxLength={1000}
            rows={4}
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setStep('sign')}
              disabled={declineSignature.isPending}
            >
              Back
            </Button>
            <Button onClick={handleDeclineConfirm} disabled={declineSignature.isPending}>
              Confirm decline
            </Button>
          </div>
          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </main>
      )}
    </div>
  );
}

interface SignSectionProps {
  data: NonNullable<ReturnType<typeof useSigningSession>['data']>;
  fields: DocumentSignFieldResponse[];
  fieldsByFile: Record<string, DocumentSignFieldResponse[]>;
  fills: Record<string, FieldFill>;
  activeFieldId: string | null;
  setActiveFieldId: (id: string | null) => void;
  setFills: React.Dispatch<React.SetStateAction<Record<string, FieldFill>>>;
  requiredFieldsRemaining: number;
  submitting: boolean;
  submitError: string | null;
  message: string | null;
  onSubmit: () => void;
  onDecline: () => void;
  sectionRef?: React.RefObject<HTMLElement | null>;
}

function SignSection({
  data,
  fields,
  fieldsByFile,
  fills,
  activeFieldId,
  setActiveFieldId,
  setFills,
  requiredFieldsRemaining,
  submitting,
  submitError,
  message,
  onSubmit,
  onDecline,
  sectionRef,
}: SignSectionProps) {
  const activeField = fields.find((f) => f.fieldId === activeFieldId) ?? null;
  const padOpen =
    !!activeField && (activeField.type === 'signature' || activeField.type === 'initial');

  return (
    <main
      ref={sectionRef}
      tabIndex={-1}
      className="max-w-5xl mx-auto px-4 py-6 space-y-6 outline-none"
      aria-label="Sign documents"
    >
      <div className="sr-only" aria-live="polite" role="status">
        {submitting
          ? 'Submitting your signature'
          : requiredFieldsRemaining > 0
            ? `${requiredFieldsRemaining} required field${
                requiredFieldsRemaining === 1 ? '' : 's'
              } remaining`
            : 'All required fields complete. Ready to submit.'}
      </div>
      {message && (
        <section className="border rounded-md p-4 bg-muted/40">
          <h2 className="text-sm font-semibold mb-1">Message from {data.envelope.ownerName}</h2>
          <p className="text-sm whitespace-pre-wrap">{message}</p>
        </section>
      )}

      {data.recipient.deliveryEligible && (
        <DeliveryHolderSection
          recipientId={data.recipient.recipientId}
          initialHolderId={data.recipient.deliveryHolderId}
        />
      )}

      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {requiredFieldsRemaining > 0
            ? `${requiredFieldsRemaining} required field${
                requiredFieldsRemaining === 1 ? '' : 's'
              } remaining`
            : 'All required fields complete'}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onDecline} disabled={submitting}>
            Decline
          </Button>
          <Button onClick={onSubmit} disabled={submitting || requiredFieldsRemaining > 0}>
            {submitting ? 'Submitting…' : 'Finish signing'}
          </Button>
        </div>
      </div>

      {submitError && (
        <p className="text-sm text-destructive" role="alert">
          {submitError}
        </p>
      )}

      {data.files.map((file) => (
        <FileSigningView
          key={file.fileId}
          file={file}
          fields={fieldsByFile[file.fileId] ?? []}
          fills={fills}
          setFills={setFills}
          onActivateField={setActiveFieldId}
        />
      ))}

      <SignaturePad
        open={padOpen}
        purpose={activeField?.type === 'initial' ? 'initial' : 'signature'}
        defaultName={
          activeField?.type === 'initial'
            ? deriveInitials(data.recipient.name ?? '')
            : (data.recipient.name ?? '')
        }
        onCancel={() => setActiveFieldId(null)}
        onSave={(dataUrl) => {
          if (!activeField) return;
          setFills((prev) => ({
            ...prev,
            [activeField.fieldId]: { ...prev[activeField.fieldId], dataUrl },
          }));
          setActiveFieldId(null);
        }}
      />
    </main>
  );
}

function FileSigningView({
  file,
  fields,
  fills,
  setFills,
  onActivateField,
}: {
  file: NonNullable<ReturnType<typeof useSigningSession>['data']>['files'][number];
  fields: DocumentSignFieldResponse[];
  fills: Record<string, FieldFill>;
  setFills: React.Dispatch<React.SetStateAction<Record<string, FieldFill>>>;
  onActivateField: (id: string | null) => void;
}) {
  const { doc, error } = usePdfDocument(file.downloadUrl);
  const totalPages = file.pageCount ?? doc?.numPages ?? 0;
  const pages = totalPages > 0 ? Array.from({ length: totalPages }, (_, i) => i + 1) : [];

  if (error) {
    return (
      <section className="border rounded-md p-4 text-sm text-destructive">
        Failed to load {file.fileName}: {error}
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">{file.fileName}</h3>
      <div className="space-y-4">
        {pages.map((page) => {
          const pageFields = fields.filter((f) => f.page === page);
          return (
            <PdfPageView key={page} pdfDoc={doc} pageNumber={page} width={PAGE_RENDER_WIDTH}>
              {(rendered) => (
                <>
                  {pageFields.map((field) => {
                    const fill = fills[field.fieldId];
                    return (
                      <SigningFieldOverlay
                        key={field.fieldId}
                        field={field}
                        fill={fill}
                        rendered={rendered}
                        onActivate={() => onActivateField(field.fieldId)}
                        onValueChange={(value) =>
                          setFills((prev) => ({
                            ...prev,
                            [field.fieldId]: { ...prev[field.fieldId], value },
                          }))
                        }
                      />
                    );
                  })}
                </>
              )}
            </PdfPageView>
          );
        })}
      </div>
    </section>
  );
}

function SigningFieldOverlay({
  field,
  fill,
  rendered,
  onActivate,
  onValueChange,
}: {
  field: DocumentSignFieldResponse;
  fill: FieldFill | undefined;
  rendered: { width: number; height: number };
  onActivate: () => void;
  onValueChange: (value: string) => void;
}) {
  const left = field.x * rendered.width;
  const top = field.y * rendered.height;
  const width = field.w * rendered.width;
  const height = field.h * rendered.height;

  const filled =
    field.type === 'signature' || field.type === 'initial' ? !!fill?.dataUrl : !!fill?.value;

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left,
    top,
    width,
    height,
    border: filled ? '1px solid #16a34a' : '1px dashed #2563eb',
    background: filled ? 'rgba(22,163,74,0.12)' : 'rgba(37,99,235,0.10)',
    borderRadius: 4,
  };

  if (field.type === 'signature' || field.type === 'initial') {
    return (
      <button
        type="button"
        style={baseStyle}
        className="text-xs flex items-center justify-center hover:bg-blue-200/40"
        onClick={onActivate}
        aria-label={`${field.type} field, ${filled ? 'signed' : 'click to sign'}`}
      >
        {fill?.dataUrl ? (
          <img
            src={fill.dataUrl}
            alt=""
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span>{field.type === 'signature' ? 'Sign here' : 'Initial'}</span>
        )}
      </button>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <label
        style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <input
          type="checkbox"
          checked={fill?.value === 'true'}
          onChange={(e) => onValueChange(e.target.checked ? 'true' : 'false')}
          aria-label={`${field.type} field`}
        />
      </label>
    );
  }

  if (field.type === 'date_signed') {
    return (
      <div
        style={{ ...baseStyle, padding: 4, fontSize: 12, display: 'flex', alignItems: 'center' }}
      >
        {fill?.value || new Date().toISOString().split('T')[0]}
      </div>
    );
  }

  if (field.type === 'signer_name') {
    // Editable input pre-seeded by the page-level effect with the recipient's
    // name. Editable rather than a read-only display so an email-only signer
    // (no name on the recipient row) can fill the field at sign time —
    // otherwise the field is stuck empty and Finish Signing stays disabled.
    return (
      <input
        type="text"
        value={fill?.value ?? ''}
        onChange={(e) => onValueChange(e.target.value)}
        style={{ ...baseStyle, padding: 4, fontSize: 12 }}
        aria-label={`name field${field.required ? ', required' : ''}`}
      />
    );
  }

  // email / company / title / number / text — a free-text input. Email and
  // number get the matching input type for better keyboards / validation.
  // The placeholder doubles as a label so the recipient can tell at a glance
  // what each box is asking for — without it, blank boxes are
  // indistinguishable across types.
  const inputType = field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : 'text';
  const placeholder = FIELD_PLACEHOLDER[field.type] ?? '';
  return (
    <input
      type={inputType}
      value={fill?.value ?? ''}
      onChange={(e) => onValueChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...baseStyle, padding: 4, fontSize: 12 }}
      aria-label={`${field.type} field${field.required ? ', required' : ''}`}
    />
  );
}

/**
 * Placeholder text for the text-collection field types — surfaces what
 * each box is asking for without needing an external label. Keyed by the
 * field type so the renderer just looks it up. Limited to the types the
 * fall-through input actually handles.
 */
const FIELD_PLACEHOLDER: Partial<Record<DocumentSignFieldResponse['type'], string>> = {
  email: 'Email',
  company: 'Company',
  title: 'Title',
  number: 'Number',
  text: 'Text',
};

/**
 * Optional "where should we deliver your signed copy?" step. Surfaced only when
 * the bootstrap response marks the recipient `deliveryEligible` (i.e. they map
 * to a Fias user). Choosing a holder saves an owned copy of the completed PDF
 * into that Vault workspace; skipping it is fine — the signer still gets the
 * emailed copy and the token download. The POST is authenticated, so if the
 * visitor is browsing anonymously we surface a log-in prompt instead.
 */
function DeliveryHolderSection({
  recipientId,
  initialHolderId,
}: {
  recipientId: string;
  initialHolderId: string | null;
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const switcher = useHolderSwitcher({ defaultToPersonal: true });
  const setDeliveryHolder = useSetDeliveryHolder(recipientId);
  const [savedHolderId, setSavedHolderId] = useState<string | null>(initialHolderId);

  const handleChange = useCallback(
    (holderId: string | null) => {
      switcher.setSelectedHolderId(holderId);
      if (!holderId) return;
      setDeliveryHolder.mutate(holderId, {
        onSuccess: (res) => setSavedHolderId(res.recipient.deliveryHolderId),
      });
    },
    [switcher, setDeliveryHolder],
  );

  return (
    <section className="border rounded-md p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold">Where should we deliver your signed copy?</h2>
        <p className="text-xs text-muted-foreground">
          Optional — you'll receive the completed document by email regardless.
        </p>
      </div>

      {authLoading ? (
        <p className="text-sm text-muted-foreground">Checking your account…</p>
      ) : !isAuthenticated ? (
        <p className="text-sm text-muted-foreground">
          <a href="/login" className="underline font-medium">
            Log in
          </a>{' '}
          to save a copy to your Vault, or continue and just receive the email.
        </p>
      ) : (
        <>
          <HolderSwitcher
            selectedHolderId={switcher.selectedHolderId ?? savedHolderId}
            onChange={(holderId) => handleChange(holderId)}
            hideCreate
          />
          {setDeliveryHolder.isPending && (
            <p className="text-xs text-muted-foreground">Saving your choice…</p>
          )}
          {setDeliveryHolder.isError && (
            <p className="text-xs text-destructive" role="alert">
              {setDeliveryHolder.error instanceof Error
                ? setDeliveryHolder.error.message
                : 'Could not save your delivery workspace'}
            </p>
          )}
          {savedHolderId && !setDeliveryHolder.isPending && !setDeliveryHolder.isError && (
            <p className="text-xs text-muted-foreground">
              Your signed copy will be saved to this workspace.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Initials from a display name. "John Doe" → "JD", "Mary Anne Smith" → "MAS",
 * "cher" → "C". Splits on whitespace, takes the first letter of each part,
 * uppercases, and caps at 4 characters so an absurd middle-name chain
 * doesn't overflow the initial-field box. Returns an empty string for an
 * empty / whitespace-only input — the pad's "Type" tab then just renders
 * empty, which is the right cue that the recipient should type their own.
 */
function deriveInitials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .map((p) => p.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  if (parts.length === 0) return '';
  return parts
    .map((p) => p.charAt(0).toUpperCase())
    .slice(0, 4)
    .join('');
}

function CenteredMessage({ title, description }: { title: string; description?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-2">
        <h1 className="text-xl font-semibold">{title}</h1>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
}

/**
 * Post-submit confirmation — also shown when a recipient returns to their
 * signing link after signing. Polls the recipient's sealed-download endpoint
 * every 5s until it returns a presigned URL. The seal pipeline runs after
 * submitRecipient commits, so on a parallel envelope this resolves within a
 * few seconds; until every party has signed it shows the "available once
 * everyone has signed" copy and a re-check button.
 */
function SubmittedConfirmation({ token }: { token: string }) {
  const sealed = useRecipientSealedDownload(token, { refetchInterval: 5_000 });
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-xl font-semibold">Thanks for signing</h1>
        <p className="text-sm text-muted-foreground">
          Your signature has been recorded. Keep this link — once everyone has signed, your
          completed copy will be available to download here.
        </p>
        {sealed.data && (
          <a
            href={sealed.data.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium underline"
          >
            Download your signed copy
          </a>
        )}
        {!sealed.data && (
          <Button variant="outline" size="sm" onClick={() => sealed.refetch()}>
            Check again
          </Button>
        )}
      </div>
    </div>
  );
}
